/**
 * FE-T02 — Admin Sub-Delegates API service
 *
 * Wraps the Phase-6 admin-managed CRUD endpoints (P6-T08/T09):
 *   GET    /admin/users/:ownerId/sub-delegates              listSubDelegates
 *   POST   /admin/users/:ownerId/sub-delegates              createSubDelegate
 *   POST   /admin/users/:ownerId/sub-delegates/:id/resend   resendSubDelegateInvite
 *   DELETE /admin/users/:ownerId/sub-delegates/pending/:id  cancelSubDelegateInvite
 *   DELETE /admin/users/:ownerId/sub-delegates/:id          revokeSubDelegateRelationship
 *
 * Used by `useAdminSubDelegates` hooks and the `SubDelegatesModal` UI.
 */

import { apiClient } from '@/core/api/client';
import type {
  CreateSubDelegatePayload,
  DelegationPermission,
  InviteSubDelegateResponse,
  RevokeRelationshipResponse,
  SubDelegateListResponse,
  UpdatePermissionsResponse,
  UpdateSubDelegateLimitResponse,
} from '../types/sub-delegate.type';

const basePath = (ownerId: string) => `/admin/users/${ownerId}/sub-delegates`;

export async function listSubDelegates(
  ownerId: string,
): Promise<SubDelegateListResponse> {
  const response = await apiClient.get<SubDelegateListResponse>(basePath(ownerId));
  return response.data;
}

export async function createSubDelegate(
  ownerId: string,
  payload: CreateSubDelegatePayload,
): Promise<InviteSubDelegateResponse> {
  const response = await apiClient.post<InviteSubDelegateResponse>(
    basePath(ownerId),
    payload,
  );
  return response.data;
}

export async function resendSubDelegateInvite(
  ownerId: string,
  pendingId: string,
): Promise<InviteSubDelegateResponse> {
  const response = await apiClient.post<InviteSubDelegateResponse>(
    `${basePath(ownerId)}/${pendingId}/resend`,
  );
  return response.data;
}

export async function cancelSubDelegateInvite(
  ownerId: string,
  pendingId: string,
): Promise<void> {
  await apiClient.delete(`${basePath(ownerId)}/pending/${pendingId}`);
}

export async function revokeSubDelegateRelationship(
  ownerId: string,
  relationshipId: string,
): Promise<RevokeRelationshipResponse> {
  const response = await apiClient.delete<RevokeRelationshipResponse>(
    `${basePath(ownerId)}/${relationshipId}`,
  );
  return response.data;
}

export async function updateSubDelegatePermissions(
  ownerId: string,
  relationshipId: string,
  permissions: DelegationPermission[],
): Promise<UpdatePermissionsResponse> {
  const response = await apiClient.patch<UpdatePermissionsResponse>(
    `${basePath(ownerId)}/${relationshipId}/permissions`,
    { permissions },
  );
  return response.data;
}

/**
 * Overrides `users.sub_delegate_limit` for an owner.
 * `null` reverts to the platform default (3). Reducing below current usage
 * is allowed — existing delegates keep access; only new invites are blocked.
 */
export async function updateSubDelegateLimit(
  ownerId: string,
  limit: number | null,
): Promise<UpdateSubDelegateLimitResponse> {
  const response = await apiClient.patch<UpdateSubDelegateLimitResponse>(
    `/admin/users/${ownerId}/sub-delegate-limit`,
    { limit },
  );
  return response.data;
}

export const adminSubDelegatesApi = {
  listSubDelegates,
  createSubDelegate,
  resendSubDelegateInvite,
  cancelSubDelegateInvite,
  revokeSubDelegateRelationship,
  updateSubDelegatePermissions,
  updateSubDelegateLimit,
};
